import { FC, useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import scrollToElement from 'utils/scroll-to-element'

import { ICoinPageCurrency } from 'config/coin-pages-list'

import styles from './styles.module.scss'
import { getFiatCurrency } from './utils'
import { useTranslation } from 'utils/use-translation'
import { ICoinPageData } from '../../assets/page-data/types'

export const HowToLend: FC<{
  coinPage?: ICoinPageData
  pageCurrency: ICoinPageCurrency
}> = ({ coinPage, pageCurrency }) => {
  const { t } = useTranslation()
  const widgetRef = useRef(null)

  const [activeTab, setActiveTab] = useState(0)
  const [closedTab, setClosedTab] = useState(null)

  const toggleTab = useCallback(
    (id) => {
      return function () {
        setClosedTab(activeTab !== id ? activeTab : id)
        setActiveTab(activeTab !== id ? id : 0)
      }
    },
    [activeTab, closedTab],
  )

  const handleScrollToHeader = useCallback(() => {
    scrollToElement('header')
  }, [])

  useEffect(() => {
    const currency = getFiatCurrency(pageCurrency.code.toUpperCase())

    widgetRef.current.innerHTML = ''
    const script = document.createElement('script')
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js'
    script.async = true
    script.innerHTML = `{
      "symbol": "${pageCurrency.code.toUpperCase()}${currency}",
      "width": "100%",
      "height": "100%",
      "locale": "en",
      "dateRange": "12M",
      "colorTheme": "light",
      "trendLineColor": "rgba(255, 149, 7, 1)",
      "underLineColor": "rgba(246, 178, 107, 0.78)",
      "underLineBottomColor": "rgba(249, 203, 156, 0)",
      "isTransparent": false,
      "autosize": false,
      "largeChartUrl": ""
    }`
    widgetRef.current.appendChild(script)
  }, [pageCurrency.code])

  return (
    <div id="how-it-works" className={styles.block}>
      <div className={cn('container', styles.blockWrapper)}>
        <div className={styles.content}>
          <h2 className={styles.title}>
            {coinPage?.howLend?.title 
              ? t(coinPage?.howLend?.title)
              : <>{t('how_lend')} <span>{pageCurrency?.customFields?.lendHeading || pageCurrency.name}</span></>}
          </h2>
          <p className={styles.subheadingText}>
            {t('we_make_it_simple')}{' '}
            {pageCurrency?.customFields?.lendSubHeading
              ? pageCurrency?.customFields?.lendSubHeading
              : pageCurrency.name}{' '}
            {t('loan')}.
          </p>
          <div className={styles.guideListWrapper}>
            <div className={styles.guideList}>
              <div className={cn(styles.tab, { [styles.tabActive]: activeTab === 0 })}>
                <div className={cn(styles.number, { [styles.openedTabNumber]: activeTab === 0 })}>
                  <span onClick={toggleTab(0)}>1</span>
                  <div />
                </div>
                <div className={styles.tabContent}>
                  <span className={styles.step} onClick={toggleTab(0)}>{t('calculate_crypto_loan')}</span>
                  <p className={cn({ [styles.openedTab]: activeTab === 0 }, { [styles.closedTab]: closedTab === 0 })}>
                    {coinPage?.howLend?.steps?.calculate?.text 
                      ? t(coinPage?.howLend?.steps?.calculate?.text)
                      : <>
                      {t('set_up_loan')} {pageCurrency?.customFields?.lendInfoName || pageCurrency.name} {t('confirm_with_phone_email')}
                    </>}
                  </p>
                </div>
              </div>

              <div className={cn(styles.tab, { [styles.tabActive]: activeTab === 1 })}>
                <div className={cn(styles.number, { [styles.openedTabNumber]: activeTab === 1 })}>
                  <span onClick={toggleTab(1)}>2</span>
                  <div />
                </div>
                <div className={styles.tabContent}>
                  <span className={styles.step} onClick={toggleTab(1)}>
                    {t('get_crypto_loan')}
                  </span>
                  <p className={cn({ [styles.openedTab]: activeTab === 1 }, { [styles.closedTab]: closedTab === 1 })}>
                    {coinPage?.howLend?.steps?.get_loan?.text 
                      ? t(coinPage?.howLend?.steps?.get_loan?.text)
                      : t('send_collateral_we_transfer_loan')
                    }
                  </p>
                </div>
              </div>

              <div className={cn(styles.tab, { [styles.tabActive]: activeTab === 2 })}>
                <div className={cn(styles.number, { [styles.openedTabNumber]: activeTab === 2 })}>
                  <span onClick={toggleTab(2)}>3</span>
                  <div />
                </div>
                <div className={styles.tabContent}>
                  <span className={styles.step} onClick={toggleTab(2)}>
                    {t('start_spending_step')}
                  </span>
                  <p className={cn({ [styles.openedTab]: activeTab === 2 }, { [styles.closedTab]: closedTab === 2 })}>
                    {coinPage?.howLend?.steps?.start_spending?.text 
                      ? t(coinPage?.howLend?.steps?.start_spending?.text)
                      : t('use_loan_we_will_keep_informed')
                    }
                  </p>
                </div>
              </div>

              <div className={cn(styles.tab, { [styles.tabActive]: activeTab === 3 })}>
                <div className={cn(styles.number, { [styles.openedTabNumber]: activeTab === 3 })}>
                  <span onClick={toggleTab(3)}>4</span>
                </div>
                <div className={styles.tabContent}>
                  <span className={styles.step} onClick={toggleTab(3)}>
                    {coinPage?.howLend?.steps?.get_back?.title 
                      ? t(coinPage?.howLend?.steps?.get_back?.title)
                      : <>{t('get_your')} {pageCurrency?.customFields?.lendStepHeading || pageCurrency.name} {t('back')}</>
                    }
                  </span>
                  <p className={cn({ [styles.openedTab]: activeTab === 3 }, { [styles.closedTab]: closedTab === 3 })}>
                    {coinPage?.howLend?.steps?.get_back?.text 
                      ? t(coinPage?.howLend?.steps?.get_back?.text)
                      : t('you_can_repay_collateral')}
                  </p>
                </div>
              </div>
            </div>
            <button className={styles.button} type="button" onClick={handleScrollToHeader}>{t('get_loan')}</button>
          </div>
        </div>
        <div className={styles.widgetBox}>
          <div className="tradingview-widget-container" ref={widgetRef}></div>
        </div>
      </div>
    </div>
  )
}
